import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Anchor,
  Droplet,
  Droplets,
  Fish,
  Flower2,
  Landmark,
  MapPin,
  Mountain,
  MountainSnow,
  PawPrint,
  Sailboat,
  Ship,
  Thermometer,
  Trees,
  Umbrella,
  Waves,
} from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import SituationMap from "./SituationMap";
import { useLang } from "@/i18n";

/* Icône thématique par activité (ordre : sud 0–9 puis nord 10–15) */
const ACTIVITY_ICONS = [
  Anchor, Fish, Thermometer, Mountain, Landmark, MountainSnow, Droplets, Droplet,
  MapPin, Ship, Waves, Flower2, PawPrint, Trees, Umbrella, Sailboat,
];

const wx = (id, w, h) =>
  `https://static.wixstatic.com/media/${id}/v1/fill/w_${w},h_${h},q_90/${id}`;

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/search/Villa+Mana+Gourbeyre+Guadeloupe";

const IMG = [
  "https://images.pexels.com/photos/11002975/pexels-photo-11002975.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=600&h=450&fit=crop",
  "https://images.pexels.com/photos/13644018/pexels-photo-13644018.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.pexels.com/photos/30491755/pexels-photo-30491755.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1663312210776-8ab35d4cd3dd?q=80&w=600&h=450&fit=crop",
  "https://images.pexels.com/photos/10416507/pexels-photo-10416507.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1767652216266-73077b43b5d0?q=80&w=600&h=450&fit=crop",
  "https://images.pexels.com/photos/3647545/pexels-photo-3647545.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1522679056866-8dbbc8774a9d?q=80&w=600&h=450&fit=crop",
  "/saintes-terre-de-haut.jpg",
  "https://images.pexels.com/photos/38208422/pexels-photo-38208422.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1527017895487-3ac53c0f03fa?q=80&w=600&h=450&fit=crop",
  "https://images.pexels.com/photos/10879786/pexels-photo-10879786.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1592930506084-71a406f242a1?q=80&w=600&h=450&fit=crop",
  "https://images.pexels.com/photos/7512632/pexels-photo-7512632.jpeg?auto=compress&cs=tinysrgb&w=600&h=450",
  "https://images.unsplash.com/photo-1568430462989-44163eb1752f?q=80&w=600&h=450&fit=crop",
];

function Group({ title, items, testid, start = 0 }) {
  return (
    <div data-testid={testid} className="mt-16">
      <Reveal>
        <h3 className="font-display text-2xl sm:text-3xl font-light text-bone">
          {title}
        </h3>
      </Reveal>
      <div className="mt-6 grid sm:grid-cols-2 gap-x-10">
        {items.map((a, i) => (
          <Reveal key={i} delay={0.04 * i}>
            <div
              data-testid={`${testid}-item-${i}`}
              className="group border-t gold-hairline py-5 px-2 -mx-2 hover:bg-moss/40 transition-colors duration-500 flex gap-4 sm:gap-5 items-start"
            >
              <div className="w-20 sm:w-28 shrink-0 overflow-hidden">
                <img
                  src={IMG[start + i]}
                  alt={a.t}
                  loading="lazy"
                  data-testid={`${testid}-photo-${i}`}
                  style={{ transition: "transform 1.2s cubic-bezier(0.22,1,0.36,1)" }}
                  className="w-full aspect-[4/3] object-cover group-hover:scale-110"
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const Icon = ACTIVITY_ICONS[start + i];
                    return Icon ? <Icon size={18} className="text-gold shrink-0" aria-hidden="true" /> : null;
                  })()}
                </div>
                <p className="font-display text-xl text-bone mt-1.5">{a.t}</p>
                <p className="text-xs sm:text-sm text-sage font-light mt-1 leading-relaxed">
                  {a.d}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export default function Situation() {
  const { t } = useLang();
  return (
    <section
      id="situation"
      data-testid="situation-section"
      className="py-24 sm:py-32 px-5 sm:px-10 bg-pine/50"
    >
      <div className="mx-auto max-w-[1500px]">
        <SectionHeader eyebrow={t.situation.eyebrow} title={t.situation.title} />
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-sm sm:text-base text-mist font-light leading-relaxed">
            {t.situation.intro}
          </p>
          <div
            data-testid="situation-benchmarks"
            className="mt-8 flex flex-wrap gap-x-8 gap-y-3"
          >
            {t.situation.benchmarks.map((b, i) => (
              <span
                key={i}
                className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-goldlight border gold-hairline px-4 py-2"
              >
                {b}
              </span>
            ))}
          </div>
        </Reveal>
        <SituationMap itineraryUrl={GOOGLE_MAPS_URL} />
        <div id="activites" data-testid="activites-block">
          <Group title={t.situation.sudTitle} items={t.situation.sud} testid="situation-sud" />
          <Group title={t.situation.nordTitle} items={t.situation.nord} testid="situation-nord" start={10} />
        </div>
      </div>
    </section>
  );
}

export { GOOGLE_MAPS_URL };
